// packages/agent-kit/src/hooks/admin/finance/useFinanceData.ts

import useSWR from 'swr';
import { adminFinanceService, isApiError } from '@dopomogai/api-client';
import type { MonobankClientInfo, BalanceSnapshot } from '@dopomogai/api-client';

// A generic fetcher for our SWR hooks
const swrFetcher = async (fetcherFunction: () => Promise<any>) => {
    const response = await fetcherFunction();
    if (isApiError(response)) {
        // SWR will catch this and put it in the `error` state
        throw new Error(response.message || 'Failed to fetch data');
    }
    return response;
};

/**
 * Hook to fetch the current Monobank account balances.
 * Provides data, loading state, error state, and a manual refresh function.
 */
export const useCurrentBalance = () => {
    const { data, error, isLoading, mutate } = useSWR<MonobankClientInfo>(
        '/admin/finance/balance', // SWR key - unique identifier for the data
        () => swrFetcher(adminFinanceService.getCurrentBalance)
    );

    return {
        balanceData: data,
        isLoading,
        error,
        refresh: mutate, // Allows manual re-fetching
    };
};

/**
 * Hook to fetch the historical balance data for charts.
 */
export const useBalanceHistory = () => {
    const { data, error, isLoading } = useSWR<BalanceSnapshot[]>(
        '/admin/finance/balance-history',
        () => swrFetcher(adminFinanceService.getBalanceHistory)
    );

    return {
        history: data || [], // Default to an empty array to prevent render errors
        isLoading,
        error,
    };
};